'use client'

import { useEffect, useState } from 'react'

// useStaffTools(user): which of staff's moderation buttons this signed-in person sees (ai_app_backend plan task
// r6-moderation): `moderator`, the moderator panel on the picture page and /mod; `admin`, the trash on someone else's
// picture (the picture page's and a card's). The page only hides buttons: v1 and v2 each check the role themselves.
//
// With the `moderation` area's switch putting the person on v2 (src/lib/api/switch.ts), the roles are v2's (`roles` in
// GET /v2/me: a moderator or an admin moderates, an admin also deletes anyone's picture); for a visitor on v1, the
// build's lists, as before (NEXT_PUBLIC_MODERATOR_UIDS, NEXT_PUBLIC_ADMIN_UID). `decided` is false until it is known:
// both are false till then. Asked once per person per page load, however many cards ask, and only when signed in.
// The module that asks loads only then (`await import`), so it adds nothing to a page's first load.

const MODERATOR_UIDS = process.env.NEXT_PUBLIC_MODERATOR_UIDS
  ? process.env.NEXT_PUBLIC_MODERATOR_UIDS.split(',')
  : []
const ADMIN_UID = process.env.NEXT_PUBLIC_ADMIN_UID

// The person's roles on v2, or null for a visitor on v1, per uid for this page load.
const answers = new Map()

function rolesOf(user) {
  if (!process.env.NEXT_PUBLIC_AI_APP_API) return Promise.resolve(null)
  let answer = answers.get(user.uid)
  if (!answer) {
    answer = (async () => {
      try {
        const { staffRoles } = await import('../lib/api/moderation.ts')
        return await staffRoles(await user.getIdToken())
      } catch {
        return null // the switch or its module failed: the call goes to v1, so v1's lists apply
      }
    })()
    answers.set(user.uid, answer)
  }
  return answer
}

/** Staff's buttons from `roles` (v2's) or, when null, from the build's lists of uids (v1's). */
export function staffTools(uid, roles) {
  if (!uid) return { moderator: false, admin: false }
  if (roles === null)
    return {
      moderator: MODERATOR_UIDS.includes(uid),
      admin: !!ADMIN_UID && uid === ADMIN_UID,
    }
  const admin = roles.includes('admin')
  return { moderator: admin || roles.includes('moderator'), admin }
}

export default function useStaffTools(user) {
  const uid = user?.uid
  const [known, setKnown] = useState({ uid: undefined, roles: undefined })

  useEffect(() => {
    if (!user) return undefined
    let current = true
    rolesOf(user).then((roles) => {
      if (current) setKnown({ uid: user.uid, roles })
    })
    return () => {
      current = false
    }
  }, [user])

  if (!uid) return { decided: true, moderator: false, admin: false }
  if (known.uid !== uid)
    return { decided: false, moderator: false, admin: false }
  return { decided: true, ...staffTools(uid, known.roles) }
}
